import React, { Suspense, lazy, useEffect, useState } from 'react'
import { useAuth } from './contexts/auth-context'
import { HashRouter, Route, Routes, useLocation, useNavigationType } from 'react-router-dom'
import { Layout } from './common/layout'
import { NotFoundPage } from './common/notFoundPage'
import ErrorBoundary from './common/ErrorBoundary'
import { useUI } from './contexts/UIContext'
import { useFullscreen } from './contexts/screen-context'
import { useBrandFilter } from './contexts/brand-filter-context'
import { NotificationProvider } from './contexts/notification-context'
import DivisionSelector from './pages/divisionSelector/DivisionSelector'
import Loading from './common/Loading'
import './App.css'
import './utils/messageConfig'

// Route-level code splitting — shrinks the initial bundle and lets each
// page stream in on demand. Suspense fallback below is the branded
// `Loading` component so the hand-off feels seamless.
const HomePage = lazy(() => import('./pages/homePage/HomePage'))
const EditPage = lazy(() => import('./pages/editPage/EditPage'))
const ProjectGallery = lazy(() => import('./pages/projects/ProjectGallery'))
const ProjectDetail = lazy(() => import('./pages/projects/ProjectDetail'))
const AllDesigns = lazy(() => import('./pages/designs/AllDesigns'))
const DesignDeepLink = lazy(() => import('./pages/designs/DesignDeepLink'))
const BatchJobs = lazy(() => import('./pages/batch-jobs/BatchJobs'))
const FavoritesPage = lazy(() => import('./pages/favorites'))
const Studio = lazy(() => import('./pages/studio/Studio'))
const DesignComponent = lazy(() => import('./pages/canvas/Design'))
const KitViewerPage = lazy(() => import('./pages/kitViewer/KitViewerPage'))
const SurveyPage = lazy(() => import('./pages/survey/SurveyPage'))
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))

function App() {
  const { showSidebar, setShowSidebar } = useUI()
  const { isAuthenticated } = useAuth()
  const { isFullscreen, setIsFullscreen } = useFullscreen()
  const { isBrandSelected: isDivisionSelected } = useBrandFilter()
  const [sidebarExpanded, setSidebarExpanded] = useState(false)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)

  // Update window width when resized
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // Listen for sidebar expansion state changes from the Sidebar component
  useEffect(() => {
    const handleSidebarExpansion = (event) => {
      if (event.detail && Object.prototype.hasOwnProperty.call(event.detail, 'expanded')) {
        setSidebarExpanded(event.detail.expanded)
      }
    }

    window.addEventListener('sidebarExpansionChanged', handleSidebarExpansion)

    return () => {
      window.removeEventListener('sidebarExpansionChanged', handleSidebarExpansion)
    }
  }, [])

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle Escape key for fullscreen exit
      // Let the Canvas component handle sidebar toggling
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false)
        setShowSidebar(true)
      }
    }

    // Add the event listener when the component mounts
    window.addEventListener('keydown', handleKeyDown)

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isFullscreen, setIsFullscreen, setShowSidebar])

  // Responsive margins based on screen size and sidebar state
  const getMarginLeft = () => {
    if (windowWidth <= 768) return '0'
    if (!showSidebar) return '0'
    return sidebarExpanded ? '16rem' : '5rem'
  }

  const mainStyle = {
    marginLeft: getMarginLeft(),
    marginRight: windowWidth <= 768 ? '0' : '.5rem',
    width: windowWidth <= 768 ? '100%' : 'auto',
  }

  const showDivisionSelector = isAuthenticated && !isDivisionSelected

  return (
    <ErrorBoundary>
    <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <NotificationProvider>
        {showDivisionSelector && <DivisionSelector />}

        <div className="app-container" style={mainStyle}>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/survey/:surveyId" element={<SurveyPage />} />
            <Route
              path="*"
              element={
                <Layout>
                  <LayoutRoutes isAuthenticated={isAuthenticated} />
                </Layout>
              }
            />
          </Routes>
        </Suspense>
      </div>
      </NotificationProvider>
    </HashRouter>
    </ErrorBoundary>
  )
}

// Inner routes wrapped with a per-pathname key so the route-transition
// fade replays on every navigation. Also scrolls to top on path change so
// new pages start at the top instead of the previous scroll position.
// eslint-disable-next-line react/prop-types -- local component, one prop, used once
function LayoutRoutes({ isAuthenticated }) {
  const location = useLocation()
  const navigationType = useNavigationType()

  useEffect(() => {
    // On back/forward (POP) let the browser restore the prior scroll position.
    // Only force-scroll-top on PUSH or REPLACE.
    if (navigationType === 'POP') return
    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({ top: 0, left: 0, behavior: prefersReduced ? 'auto' : 'smooth' })
  }, [location.pathname, navigationType])

  return (
    <div className="route-transition" key={location.pathname}>
      <Routes>
        <Route path="/" element={isAuthenticated ? <HomePage /> : <Loading />} />
        <Route path="/edit/:projectId" element={isAuthenticated ? <EditPage /> : <Loading />} />
        <Route path="/canvas" element={isAuthenticated ? <DesignComponent /> : <Loading />} />
        <Route path="/projects" element={isAuthenticated ? <ProjectGallery /> : <Loading />} />
        <Route path="/projects/:projectId" element={isAuthenticated ? <ProjectDetail /> : <Loading />} />
        <Route path="/designs" element={isAuthenticated ? <AllDesigns /> : <Loading />} />
        <Route path="/batch-jobs" element={isAuthenticated ? <BatchJobs /> : <Loading />} />
        <Route path="/favorites" element={isAuthenticated ? <FavoritesPage /> : <Loading />} />
        {/* Was rendering DesignGallery with a hardcoded placeholder design, so
            every shared link to a design opened an empty gallery. The route
            component fetches the design by id first. */}
        <Route
          path="/projects/:projectId/designs/:designId"
          element={isAuthenticated ? <DesignDeepLink /> : <Loading />}
        />
        <Route path="/viewer" element={isAuthenticated ? <Studio /> : <Loading />} />
        <Route path="/studio" element={isAuthenticated ? <KitViewerPage /> : <Loading />} />
        <Route path="/admin/surveys" element={isAuthenticated ? <AdminDashboard /> : <Loading />} />
        <Route path="*" element={isAuthenticated ? <NotFoundPage /> : <Loading />} />
      </Routes>
    </div>
  )
}

export default App
